// Small dialogs used by the Documents module.

import { useEffect, useState } from "react";
import api, { formatError } from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { toast } from "sonner";
import { XCircle, UserPlus, Search } from "lucide-react";

export function RejectDialog({ open, doc, onClose, onDone }) {
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setRemarks(""); }, [open]);
  if (!doc) return null;
  const submit = async () => {
    if (!remarks.trim()) { toast.error("Rejection remarks are mandatory"); return; }
    setBusy(true);
    try {
      await api.post(`/documents/${doc.id}/reject`, { remarks: remarks.trim() });
      toast.success("Document rejected — creator has been notified");
      onDone?.(); onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="doc-reject-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <XCircle className="w-5 h-5" /> Reject document
          </DialogTitle>
          <DialogDescription>
            You are rejecting <strong>{doc.title}</strong>. The creator will be able to re-edit and resubmit.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-bold text-rose-700">Remarks (mandatory) *</Label>
          <Textarea
            rows={4} className="mt-1.5" autoFocus
            placeholder="Explain why you're rejecting — the creator will see this."
            value={remarks} onChange={(e) => setRemarks(e.target.value)}
            data-testid="doc-reject-remarks"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !remarks.trim()} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="doc-reject-submit">
            {busy ? "Rejecting…" : "Reject with remarks"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReassignDialog({ open, doc, onClose, onDone }) {
  const [candidates, setCandidates] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setSelected(""); setQ("");
    api.get("/documents/reassign/candidates")
      .then((r) => setCandidates(r.data || []))
      .catch((e) => toast.error(formatError(e)));
  }, [open]);
  if (!doc) return null;
  const filtered = candidates.filter((c) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [c.name, c.email, c.role, c.designation].some((v) => String(v || "").toLowerCase().includes(s));
  }).slice(0, 200);

  const submit = async () => {
    if (!selected) { toast.error("Pick a new owner"); return; }
    setBusy(true);
    try {
      await api.post(`/documents/${doc.id}/reassign`, { new_owner_id: selected });
      toast.success("Document reassigned");
      onDone?.(); onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="doc-reassign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-indigo-700">
            <UserPlus className="w-5 h-5" /> Reassign document
          </DialogTitle>
          <DialogDescription>
            The original creator is inactive or removed. Choose a new owner for <strong>{doc.title}</strong>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-8" placeholder="Search users…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="doc-reassign-search" />
          </div>
          <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 divide-y" data-testid="doc-reassign-candidates">
            {filtered.map((c) => (
              <label key={c.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 dark:bg-slate-800/50 ${selected === c.id ? "bg-indigo-50" : ""}`}>
                <input type="radio" name="reassign" checked={selected === c.id} onChange={() => setSelected(c.id)} />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-slate-800 dark:text-slate-200 truncate">{c.name}</div>
                  <div className="text-[10.5px] text-slate-500 dark:text-slate-400 truncate">{c.email} · {c.role}</div>
                </div>
              </label>
            ))}
            {filtered.length === 0 && <div className="text-center text-[12px] text-slate-500 dark:text-slate-400 py-4">No matches</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !selected} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="doc-reassign-submit">
            {busy ? "Reassigning…" : "Reassign ownership"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
